import type { IWellnessInviteData } from '../../interfaces';
import {
  APPLICATION_STATUS_CONFIG,
  FALLBACK_APPLICATION_STATUS_CLASS,
  FALLBACK_WELLNESS_STATUS_CLASS,
  WELLNESS_TYPE_STATUS_CONFIG,
} from '../constants';
import type { IStatusStyle } from '../constants';

const SHORT_DATE_OPTIONS = { day: 'numeric', month: 'short', year: 'numeric' } as const;

/** '2026-06-19T…' -> '19 Jun 2026'. Missing input renders as an em dash. */
export const formatShortDate = (iso: string | null | undefined): string => {
  if (!iso) return '—';
  return new Date(iso).toLocaleDateString('en-GB', SHORT_DATE_OPTIONS);
};

/**
 * Deleted openings survive as application rows with jobTitle: null;
 * a leading '*' is an internal marker and is stripped for display.
 */
export const cleanJobTitle = (jobTitle: string | null | undefined): string => {
  if (!jobTitle) return 'Untitled role';
  return jobTitle.replace(/^\*/, '');
};

/** Whole days until expiry; null when unknown. */
export const daysRemaining = (iso: string | null | undefined): number | null => {
  if (!iso) return null;
  const diffMs = new Date(iso).getTime() - Date.now();
  return Math.ceil(diffMs / (1000 * 60 * 60 * 24));
};

/**
 * The backend collapses an unknown status into 'Applied'; mirror that
 * rather than rendering a badge with no label.
 */
export const getApplicationStatus = (status?: string | null): IStatusStyle => {
  if (status && APPLICATION_STATUS_CONFIG[status]) return APPLICATION_STATUS_CONFIG[status];
  return {
    label: status || 'Applied',
    className: FALLBACK_APPLICATION_STATUS_CLASS,
  };
};

export const getWellnessTypeStatus = (status: string | null): IStatusStyle => {
  if (status && WELLNESS_TYPE_STATUS_CONFIG[status]) return WELLNESS_TYPE_STATUS_CONFIG[status];
  return {
    label: status ?? 'Unknown',
    className: FALLBACK_WELLNESS_STATUS_CLASS,
  };
};

export const isActionableInvite = (invite: IWellnessInviteData): boolean =>
  invite.types.some((t) => t.status === 'Invite Sent' || t.status === 'In Progress');

export const formatDaysLeft = (daysLeft: number | null): string | null => {
  if (daysLeft === null) return null;
  return daysLeft > 0 ? `${daysLeft}d left` : 'Due today';
};
